import React from "react";
import { Play, Square, SkipForward } from "lucide-react";
import { ExploreDegreeLegend, type ExploreDegreeItem } from "./ExploreDegreeLegend";
import type { ExplorePlaybackRange } from "../../lib/explore3nps";

export interface ExploreSandboxTransportProps {
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
  onNext: () => void;
  currentIndex: number;
  sequenceLength: number;
  bpm: number;
  onBpmChange: (bpm: number) => void;
  degrees?: readonly ExploreDegreeItem[];
  onDegreeClick?: (degree: string) => void;
  playbackRange?: ExplorePlaybackRange;
  onPlaybackRangeChange?: (range: ExplorePlaybackRange) => void;
}

export const ExploreSandboxTransport: React.FC<ExploreSandboxTransportProps> = ({
  isPlaying,
  onPlay,
  onStop,
  onNext,
  currentIndex,
  sequenceLength,
  bpm,
  onBpmChange,
  degrees = [],
  onDegreeClick,
  playbackRange = "octave",
  onPlaybackRangeChange,
}) => (
  <div className="flex w-full items-center justify-between gap-3 px-6 py-3">
    <div className="flex shrink-0 items-center gap-2">
      {!isPlaying ? (
        <button
          type="button"
          onClick={onPlay}
          disabled={sequenceLength === 0}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-emerald-200 transition-colors hover:bg-white/15 disabled:opacity-30"
          title="Reproducir secuencia"
        >
          <Play className="h-4 w-4 fill-current" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onStop}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 transition-colors hover:bg-white/15"
          title="Detener"
        >
          <Square className="h-3.5 w-3.5 fill-current" />
        </button>
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={sequenceLength === 0}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white/90 disabled:opacity-30"
        title="Siguiente nota"
      >
        <SkipForward className="h-4 w-4" />
      </button>
    </div>

    {degrees.length > 0 && (
      <div className="flex min-w-0 flex-1 items-center justify-center px-2">
        <ExploreDegreeLegend
          degrees={degrees}
          onDegreeClick={onDegreeClick}
          compact
        />
      </div>
    )}

    {onPlaybackRangeChange && (
      <div
        className="flex shrink-0 items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.04] p-0.5"
        role="group"
        aria-label="Alcance de la secuencia"
      >
        {(
          [
            { id: "octave" as const, label: "8va" },
            { id: "extend" as const, label: "Extender" },
          ] as const
        ).map(({ id, label }) => {
          const active = playbackRange === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onPlaybackRangeChange(id)}
              className={`rounded-full px-3 py-1.5 font-sans text-[10px] uppercase tracking-[0.12em] transition-all duration-300 ${
                active
                  ? "bg-white/12 text-[#c4b5fd] shadow-[0_0_12px_rgba(196,181,253,0.25)]"
                  : "text-white/40 hover:text-white/65"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    )}

    <div className="flex shrink-0 flex-col items-center gap-1 px-2">
      <span className="font-sans text-[10px] uppercase tracking-widest text-white/35">
        Secuencia
      </span>
      <span className="font-sans text-sm tabular-nums text-white/70">
        {sequenceLength > 0 ? `${Math.min(currentIndex + 1, sequenceLength)} / ${sequenceLength}` : "—"}
      </span>
    </div>

    <div className="flex shrink-0 items-center gap-2">
      <label className="font-sans text-[10px] uppercase tracking-widest text-white/35">BPM</label>
      <input
        type="range"
        min={40}
        max={140}
        value={bpm}
        onChange={(e) => onBpmChange(Number(e.target.value))}
        className="h-1 w-24 accent-emerald-400/80"
      />
      <span className="w-8 font-mono text-xs tabular-nums text-white/50">{bpm}</span>
    </div>
  </div>
);
